import * as z from 'zod'
import { ApiModel, ToManyRelation } from '~/shared/api/core'
import { CarRental, Region, Role } from '~/shared/api/index'
import { Option } from '~/shared/config/constants'
import { LocaleEnum, TimezoneCode } from '~/shared/config/enums'
import { buildI18nMessage } from '~/shared/lib/i18nMessageHelper'
import {
  dateSchema,
  enumOptionSchema,
  phoneScheme,
  stringRequiredScheme,
  uuidOptionsSchema,
} from '~/shared/lib/schemas'
import { isString } from '~/shared/lib/utils'

const attributeSchema = z.object({
  email: stringRequiredScheme.email(
    buildI18nMessage('validate.invalid email address'),
  ),
  name: stringRequiredScheme,
  phone: phoneScheme.optional().nullable(),
  telegramUserId: z.preprocess((arg) => {
    if (typeof arg == 'string' && arg.length) {
      return parseInt(arg)
    } else if (typeof arg == 'number') {
      return arg
    }
    return null
  }, z.number().nullable()),
  timezone: enumOptionSchema(TimezoneCode),
  locale: enumOptionSchema(LocaleEnum),
})
const relationsSchema = z.object({
  regionsIds: uuidOptionsSchema.min(
    1,
    buildI18nMessage('validate.required field'),
  ),
  rolesIds: uuidOptionsSchema.min(
    1,
    buildI18nMessage('validate.required field'),
  ),
  carRentalsIds: uuidOptionsSchema.min(
    1,
    buildI18nMessage('validate.required field'),
  ),
})
const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)
  .merge(relationsSchema)

export type EmployeeAttributes = z.infer<typeof attributeSchema>

export class Employee extends ApiModel<typeof schema, EmployeeAttributes> {
  static jsonApiType = 'users'
  static schema = schema

  getEmail(): string {
    return this.getAttribute('email')
  }
  getName(): string {
    return this.getAttribute('name')
  }
  getTimezone(): TimezoneCode {
    return this.getAttribute('timezone')
  }
  getLocale(): LocaleEnum {
    return this.getAttribute('locale')
  }

  regions(): ToManyRelation<Region, this> {
    return this.hasMany(Region)
  }
  getRegions(): Region[] {
    return this.getRelation('regions') ?? []
  }
  setRegions(ids: UniqueId[] | Option[]) {
    let regions: Region[] = []
    ids.map((regionId) => {
      const region = new Region()
      if (isString(regionId)) {
        region.setApiId(regionId)
        regions = regions.concat(region)
      }
    })
    this.setRelation('regions', regions)
  }

  roles(): ToManyRelation<Role, this> {
    return this.hasMany(Role)
  }
  getRoles(): Role[] {
    return this.getRelation('roles') ?? []
  }
  setRoles(ids: UniqueId[] | Option[]) {
    let roles: Role[] = []
    ids.map((roleId) => {
      const role = new Role()
      if (isString(roleId)) {
        role.setApiId(roleId)
        roles = roles.concat(role)
      }
    })
    this.setRelation('roles', roles)
  }

  carRentals(): ToManyRelation<CarRental, this> {
    return this.hasMany(CarRental)
  }
  getCarRentals(): CarRental[] {
    return this.getRelation('carRentals') ?? []
  }
  setCarRentals(ids: UniqueId[] | Option[]) {
    let carRentals: CarRental[] = []
    ids.map((roleId) => {
      const carRental = new CarRental()
      if (isString(roleId)) {
        carRental.setApiId(roleId)
        carRentals = carRentals.concat(carRental)
      }
    })
    this.setRelation('carRentals', carRentals)
  }

  getOption(): { id: UniqueId; label: string } {
    return {
      id: this.getApiId() as UniqueId,
      label: this.getName(),
    }
  }
  static async fetchOptions(search: string) {
    const response = await Employee.where('name', search).get(1)
    return response.getData().map((o) => o.getOption())
  }

  static async resetPassword(employeeId: UniqueId) {
    const url = `${Employee.getJsonApiUrl()}/${employeeId}/actions/reset-password`
    return Employee.effectiveHttpClient.post(url)
  }
}
